import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Badge, Card, Col, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faChartLine,
  faGraduationCap,
  faListCheck,
  faMoneyBillWave,
} from '@fortawesome/free-solid-svg-icons';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { getDashboardOverview } from '../../service/apiService';
import './ErpDashboard.css';

const money = (value) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0));

function KpiCard({ title, value, helper, icon, tone }) {
  return (
    <Card className={`erp-kpi erp-kpi-${tone}`}>
      <Card.Body>
        <div className="erp-kpi-icon"><FontAwesomeIcon icon={icon} /></div>
        <div>
          <span>{title}</span>
          <strong>{value}</strong>
          <small>{helper}</small>
        </div>
      </Card.Body>
    </Card>
  );
}

function ErpDashboard({ user, mode = 'staff' }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const admissionsRef = useRef(null);
  const projectsRef = useRef(null);
  const cashflowRef = useRef(null);

  useEffect(() => {
    const loadOverview = async () => {
      setLoading(true);
      try {
        const res = await getDashboardOverview();
        if (res.status === 'success') setOverview(res.data);
      } catch (error) {
        console.error('Dashboard overview error:', error);
      } finally {
        setLoading(false);
      }
    };
    loadOverview();
  }, []);

  const stats = overview?.stats || {};
  const admissionData = useMemo(() => overview?.admissions || [], [overview]);
  const projectStatusData = useMemo(() => overview?.project_status || [], [overview]);
  const cashflowData = useMemo(() => overview?.cashflow || [], [overview]);
  const courseTotal = useMemo(
    () => (overview?.courses || []).reduce((sum, item) => sum + Number(item.value || 0), 0),
    [overview]
  );

  useLayoutEffect(() => {
    if (!admissionsRef.current || !admissionData.length) return undefined;
    const root = am5.Root.new(admissionsRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { panX: false, panY: false }));
    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: 'label',
      renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 24 }),
    }));
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}) }));
    xAxis.data.setAll(admissionData);
    const series = chart.series.push(am5xy.LineSeries.new(root, {
      name: 'Admissions',
      xAxis,
      yAxis,
      valueYField: 'value',
      categoryXField: 'label',
      tooltip: am5.Tooltip.new(root, { labelText: '{categoryX}: {valueY}' }),
    }));
    series.strokes.template.setAll({ strokeWidth: 3, stroke: am5.color(0x6f4ff2) });
    series.fills.template.setAll({ visible: true, fillOpacity: 0.16, fill: am5.color(0x6f4ff2) });
    series.data.setAll(admissionData);
    series.appear(600);
    chart.appear(600, 80);
    return () => root.dispose();
  }, [admissionData]);

  useLayoutEffect(() => {
    if (!projectsRef.current || !projectStatusData.length) return undefined;
    const root = am5.Root.new(projectsRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    const chart = root.container.children.push(am5percent.PieChart.new(root, { innerRadius: am5.percent(62) }));
    const series = chart.series.push(am5percent.PieSeries.new(root, {
      valueField: 'value',
      categoryField: 'label',
      alignLabels: false,
    }));
    series.labels.template.set('forceHidden', true);
    series.ticks.template.set('forceHidden', true);
    series.data.setAll(projectStatusData);
    series.appear(600, 80);
    return () => root.dispose();
  }, [projectStatusData]);

  useLayoutEffect(() => {
    if (!cashflowRef.current || !cashflowData.length) return undefined;
    const root = am5.Root.new(cashflowRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { panX: false, panY: false }));
    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: 'label',
      renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 24 }),
    }));
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, {}) }));
    xAxis.data.setAll(cashflowData);
    [['income', 'Income', 0x16a56f], ['expense', 'Expense', 0xef4e60]].forEach(([field, name, color]) => {
      const series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name,
        xAxis,
        yAxis,
        valueYField: field,
        categoryXField: 'label',
        tooltip: am5.Tooltip.new(root, { labelText: '{name}: {valueY}' }),
      }));
      series.columns.template.setAll({ fill: am5.color(color), stroke: am5.color(color), cornerRadiusTL: 4, cornerRadiusTR: 4 });
      series.data.setAll(cashflowData);
    });
    chart.appear(600, 80);
    return () => root.dispose();
  }, [cashflowData]);

  if (loading) return <div className="erp-dashboard-loading"><Spinner animation="border" /></div>;

  return (
    <div className="erp-dashboard-page">
      <div className="erp-dashboard-heading">
        <div>
          <span>{mode === 'admin' ? 'Admin Control Center' : 'Staff Workspace'}</span>
          <h3>Dashboard</h3>
        </div>
        <div className="erp-dashboard-user">Welcome, <strong>{user?.name || 'User'}</strong></div>
      </div>

      <Row className="g-3">
        <Col xl={3} md={6}><KpiCard title="Admissions" value={stats.students || 0} helper={`${stats.today_admissions || 0} today`} icon={faGraduationCap} tone="purple" /></Col>
        <Col xl={3} md={6}><KpiCard title="Clients" value={stats.clients || 0} helper={`${stats.active_clients || 0} active`} icon={faBuilding} tone="cyan" /></Col>
        <Col xl={3} md={6}><KpiCard title="Projects" value={stats.projects || 0} helper={`${stats.ongoing_projects || 0} ongoing`} icon={faListCheck} tone="green" /></Col>
        <Col xl={3} md={6}><KpiCard title="Revenue" value={`Rs. ${money(stats.income)}`} helper={`Expense Rs. ${money(stats.expense)}`} icon={faMoneyBillWave} tone="orange" /></Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xl={8}>
          <Card className="erp-panel">
            <Card.Body>
              <div className="erp-panel-head">
                <div><h5>Admissions Trend</h5><span>Month-wise student admissions</span></div>
                <Badge bg="primary">{stats.active_courses || 0} Courses</Badge>
              </div>
              {admissionData.length ? <div className="erp-chart" ref={admissionsRef} /> : <div className="erp-empty">No admission data available</div>}
            </Card.Body>
          </Card>
        </Col>
        <Col xl={4}>
          <Card className="erp-panel">
            <Card.Body>
              <div className="erp-panel-head">
                <div><h5>Project Status</h5><span>Current delivery split</span></div>
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              {projectStatusData.length ? <div className="erp-donut" ref={projectsRef} /> : <div className="erp-empty">No project data available</div>}
              <div className="erp-status-list">
                {projectStatusData.map((item) => <div key={item.label}><span>{item.label}</span><strong>{item.value}</strong></div>)}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xl={4}>
          <Card className="erp-panel">
            <Card.Body>
              <div className="erp-panel-head"><div><h5>Course Mix</h5><span>Top active courses</span></div></div>
              <div className="erp-course-list">
                {(overview?.courses || []).map((course) => {
                  const pct = courseTotal ? Math.round((Number(course.value) / courseTotal) * 100) : 0;
                  return (
                    <div className="erp-course-item" key={course.label}>
                      <div><span>{course.label}</span><strong>{course.value}</strong></div>
                      <ProgressBar now={pct} />
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={8}>
          <Card className="erp-panel">
            <Card.Body>
              <div className="erp-panel-head"><div><h5>Cashflow Overview</h5><span>Income and expense trend</span></div></div>
              {cashflowData.length ? <div className="erp-chart-sm" ref={cashflowRef} /> : <div className="erp-empty">No transaction data available</div>}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xl={6}>
          <Card className="erp-panel">
            <Card.Body>
              <div className="erp-panel-head"><div><h5>Recent Projects</h5><span>Latest client project activity</span></div></div>
              <Table responsive hover className="erp-table">
                <thead><tr><th>Project</th><th>Client</th><th>Status</th><th>Budget</th></tr></thead>
                <tbody>
                  {(overview?.recent_projects || []).map((project) => (
                    <tr key={project.id}>
                      <td>{project.project_name}</td>
                      <td>{project.client_name || '-'}</td>
                      <td><Badge bg={project.status === 'completed' ? 'success' : project.status === 'on_hold' ? 'warning' : 'primary'}>{project.status}</Badge></td>
                      <td>Rs. {money(project.budget)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={6}>
          <Card className="erp-panel">
            <Card.Body>
              <div className="erp-panel-head"><div><h5>Recent Admissions</h5><span>Newest student registrations</span></div></div>
              <Table responsive hover className="erp-table">
                <thead><tr><th>Name</th><th>Course</th><th>Date</th><th>Phone</th></tr></thead>
                <tbody>
                  {(overview?.recent_students || []).map((student) => (
                    <tr key={student.id}>
                      <td>{student.name}</td>
                      <td>{student.course || '-'}</td>
                      <td>{student.admission_date || '-'}</td>
                      <td>{student.phone || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default ErpDashboard;
